import * as React from 'react';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { DeepReadonly } from 'utility-types';
import { InlineBadge } from '../InlineBadge';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { NavLinkListItem } from './NavLinkListItem';
import { SectionContainer } from '../../containers/Nav/Section';
import { useTranslation } from 'react-i18next';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { Feature } from 'flagged';

const { AionActivityFeedUI, AionAlertUI } = AppName;
const { Read } = ActivityAction;
const { ActivityFeed } = ActivityResourceName;

type Props = {
  organization: DeepReadonly<IOrganization>;
  notificationCount: number | null;
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
};

export const AlertsSectionComponent: React.FC<Props> = ({
  notificationCount,
  organization: { id, features },
  hasGlobalPermission,
  hasOrganizationPermission
}) => {
  const { t } = useTranslation();
  const showActivityFeed =
    !features.includes(OrganizationFeature.Client) && // Hide link for Client organizations
    features.includes(OrganizationFeature.ActivityFeed) &&
    (hasOrganizationPermission(Read, ActivityFeed) || hasGlobalPermission(Read, ActivityFeed));

  return (
    <SectionContainer
      id={AionAlertUI}
      icon={<NotificationsIcon />}
      subheader={t('components.alertsSection.alerts', 'Alerts')}
      hasNotifications={Boolean(notificationCount)}
    >
      <FeatureFlagWrapper>
        <Feature name="AionNewNotificationsSettingsMenuItem">
          {process.env.REACT_APP_NAME === AionActivityFeedUI ? (
            <NavLinkListItem inset to={`/${id}/activity-feed/notification-settings`}>
              {t('components.alertsSection.notificationSettings', 'Notification Settings')}
            </NavLinkListItem>
          ) : (
            <AnchorListItem inset href={`/organizations/${id}/activity-feed/notification-settings`}>
              {t('components.alertsSection.notificationSettings', 'Notification Settings')}
            </AnchorListItem>
          )}
        </Feature>
      </FeatureFlagWrapper>
      {
        <FeatureFlagWrapper>
          <Feature
            name="AionNewNotificationsSettingsMenuItem"
            render={(isEnabled: boolean) => (
              <AnchorListItem inset href="/SSQV4/Admin/UserProfile.aspx">
                {isEnabled
                  ? t('components.alertsSection.notificationSettingsLegacy', 'Notification Settings (legacy)')
                  : t('components.alertsSection.notificationSettings', 'Notification Settings')}
              </AnchorListItem>
            )}
          />
        </FeatureFlagWrapper>
      }
      {showActivityFeed &&
        (process.env.REACT_APP_NAME === AionActivityFeedUI ? (
          <NavLinkListItem inset to={`/${id}/activity-feed`}>
            {t('components.alertsSection.activityFeed', 'Activity Feed')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${id}/activity-feed`}>
            {t('components.alertsSection.activityFeed', 'Activity Feed')}
          </AnchorListItem>
        ))}
      {process.env.REACT_APP_NAME === AionAlertUI ? (
        <NavLinkListItem inset to={`/${id}/alerts/tasks/all`}>
          <InlineBadge badgeContent={notificationCount}>
            {t('components.alertsSection.allTasks', 'All Tasks')}
          </InlineBadge>
        </NavLinkListItem>
      ) : (
        <AnchorListItem inset href={`/organizations/${id}/alerts/tasks/all`}>
          <InlineBadge badgeContent={notificationCount}>
            {t('components.alertsSection.allTasks', 'All Tasks')}
          </InlineBadge>
        </AnchorListItem>
      )}
      {features.includes(OrganizationFeature.Client) &&
        (process.env.REACT_APP_NAME === AionAlertUI ? (
          <NavLinkListItem inset to={`/${id}/alerts/taskGroups/all`}>
            {t('components.alertsSection.groupedTasks', 'Grouped Tasks')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${id}/alerts/taskGroups/all`}>
            {t('components.alertsSection.groupedTasks', 'Grouped Tasks')}
          </AnchorListItem>
        ))}
    </SectionContainer>
  );
};
